import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const trainingPerformanceComponent: ComponentDef = {
  id: 'training-performance',
  name: 'Training Performance',
  description: 'Profile throughput, memory bandwidth, and FLOP utilization during training.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'batch_size', label: 'Batch size', type: 'number', default: 4, min: 1, max: 128, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'calculator',
      options: [
        { label: 'Calculator', value: 'calculator' },
        { label: 'Loss Curve', value: 'loss_curve' },
        { label: 'Gradient Monitor', value: 'gradient_monitor' },
        { label: 'Step Time Breakdown', value: 'step_time' },
        { label: 'Anomaly Diagnostics', value: 'anomaly_diag' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const batch_size = Number(params.batch_size);
    const variant = String(params.variant);

    const g = new GraphBuilder();

    if (variant === 'loss_curve') {
      // Loss curve: track loss over training steps
      g.input('batch', [batch_size, seq_len, d_model]);
      g.linear('forward', 'batch', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.linear('logits', 'act', [d_model, d_model]);
      g.input('targets', [batch_size, seq_len]);
      g.loss('loss', 'logits', 'targets');
      g.scale('ema_loss', 'loss', 'EMA Smoothing');
      g.metric('train_loss', 'ema_loss', 'Train Loss');
      g.metric('val_loss', 'train_loss', 'Val Loss');
      g.metric('divergence', 'val_loss', 'Train-Val Gap');
      g.output('out', 'divergence');
    } else if (variant === 'gradient_monitor') {
      // Gradient monitor: track gradient norms and distributions
      g.input('batch', [batch_size, seq_len, d_model]);
      g.linear('forward', 'batch', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.linear('logits', 'act', [d_model, d_model]);
      g.input('targets', [batch_size, seq_len]);
      g.loss('loss', 'logits', 'targets');
      g.linear('backward', 'loss', [d_model, d_model]);
      g.norm('grad_norm', 'backward', 'L2 Norm');
      g.scale('grad_clip', 'grad_norm', 'Gradient Clipping');
      g.metric('max_grad', 'grad_clip', 'Max Gradient');
      g.metric('dead_neurons', 'max_grad', 'Dead Neurons %');
      g.output('out', 'dead_neurons');
    } else if (variant === 'step_time') {
      // Step time breakdown: where time goes per training step
      g.input('batch', [batch_size, seq_len, d_model]);
      g.scale('data_load', 'batch', 'Data Load');
      g.linear('forward', 'data_load', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.linear('logits', 'act', [d_model, d_model]);
      g.input('targets', [batch_size, seq_len]);
      g.loss('loss', 'logits', 'targets');
      g.linear('backward', 'loss', [d_model, d_model]);
      g.allreduce('grad_sync', 'backward', [d_model, d_model]);
      g.scale('optimizer_step', 'grad_sync', 'Optimizer Step');
      g.metric('step_time', 'optimizer_step', 'Step Time (ms)');
      g.output('out', 'step_time');
    } else if (variant === 'anomaly_diag') {
      // Anomaly diagnostics: detect training instabilities
      g.input('batch', [batch_size, seq_len, d_model]);
      g.linear('forward', 'batch', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.linear('logits', 'act', [d_model, d_model]);
      g.input('targets', [batch_size, seq_len]);
      g.loss('loss', 'logits', 'targets');
      g.linear('backward', 'loss', [d_model, d_model]);
      g.norm('grad_norm', 'backward', 'L2 Norm');
      g.scale('loss_spike', 'grad_norm', 'Loss Spike Detect');
      g.scale('nan_check', 'loss_spike', 'NaN/Inf Check');
      g.metric('anomaly_score', 'nan_check', 'Anomaly Score');
      g.output('out', 'anomaly_score');
    } else {
      // Calculator (default): throughput and MFU
      g.input('batch', [batch_size, seq_len, d_model]);
      g.linear('forward', 'batch', [d_model, d_model]);
      g.activation('act', 'forward', 'GELU');
      g.linear('logits', 'act', [d_model, d_model]);
      g.input('targets', [batch_size, seq_len]);
      g.loss('loss', 'logits', 'targets');
      g.linear('backward', 'loss', [d_model, d_model]);
      g.metric('throughput', 'backward', 'tokens/s');
      g.metric('mfu', 'throughput', 'MFU %');
      g.output('out', 'mfu');
    }

    return g.build();
  }
};
